import type { Circuit } from '../engine/circuit';
import type { Element } from '../engine/element';
import { GRID, drawElement, elementBounds, geometry, valueLabel, type DrawCtx } from './symbols';

export interface ViewState {
  ox: number;
  oy: number;
  scale: number;
}

export interface RenderOptions {
  showValues: boolean;
  showHeat: boolean;
  showVoltage: boolean;
  showCurrent: boolean;
  running: boolean;
}

export interface Overlay {
  selected: Set<Element>;
  hover: Element | null;
  preview: Element | null;
  rubber: { x1: number; y1: number; x2: number; y2: number } | null;
  cursorGrid: { x: number; y: number } | null;
}

const COL = {
  bg: '#0d1220',
  grid: '#1b2740',
  gridStrong: '#243352',
  neutral: '#93a2bd',
  select: '#facc15',
  hover: '#60a5fa',
  text: '#94a3b8',
  junction: '#cbd5e1',
};

export class Renderer {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  view: ViewState = { ox: 0, oy: 0, scale: 1 };
  options: RenderOptions = { showValues: true, showVoltage: true, showCurrent: true, showHeat: true, running: false };
  private phases = new Map<string, number>();

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d context 를 만들 수 없어요');
    this.ctx = ctx;
  }

  resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    this.canvas.width = Math.max(1, Math.round(rect.width * dpr));
    this.canvas.height = Math.max(1, Math.round(rect.height * dpr));
  }

  get width(): number {
    return this.canvas.getBoundingClientRect().width;
  }
  get height(): number {
    return this.canvas.getBoundingClientRect().height;
  }

  screenToWorld(sx: number, sy: number): { x: number; y: number } {
    return { x: (sx - this.view.ox) / this.view.scale, y: (sy - this.view.oy) / this.view.scale };
  }
  screenToGrid(sx: number, sy: number): { x: number; y: number } {
    const w = this.screenToWorld(sx, sy);
    return { x: Math.round(w.x / GRID), y: Math.round(w.y / GRID) };
  }
  worldToScreen(wx: number, wy: number): { x: number; y: number } {
    return { x: wx * this.view.scale + this.view.ox, y: wy * this.view.scale + this.view.oy };
  }

  zoomAt(sx: number, sy: number, factor: number): void {
    const before = this.screenToWorld(sx, sy);
    this.view.scale = Math.min(3.5, Math.max(0.3, this.view.scale * factor));
    const after = this.screenToWorld(sx, sy);
    this.view.ox += (after.x - before.x) * this.view.scale;
    this.view.oy += (after.y - before.y) * this.view.scale;
  }

  /** 전류 애니메이션 위상 진행 */
  advance(dtSeconds: number, circuit: Circuit): void {
    if (!this.options.showCurrent || !this.options.running) return;
    for (const el of circuit.elements) {
      const speed = dotSpeed(el.current);
      if (speed === 0) continue;
      const prev = this.phases.get(el.id) ?? 0;
      this.phases.set(el.id, prev + speed * dtSeconds);
    }
  }

  render(circuit: Circuit, ov: Overlay): void {
    const { ctx } = this;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = COL.bg;
    ctx.fillRect(0, 0, this.width, this.height);

    this.drawGrid();

    ctx.save();
    ctx.translate(this.view.ox, this.view.oy);
    ctx.scale(this.view.scale, this.view.scale);

    const maxV = Math.max(circuit.maxAbsVoltage(), 1e-6);

    // 소자
    for (const el of circuit.elements) {
      const selected = ov.selected.has(el);
      const d: DrawCtx = {
        ctx,
        color: selected ? COL.select : ov.hover === el ? COL.hover : el.burnt ? '#6b5e55' : COL.neutral,
        selected,
        highlighted: ov.hover === el,
        showValues: this.options.showValues,
        running: this.options.running,
      };
      if (this.options.showVoltage && this.options.running) {
        const pins = el.pins;
        const colors = pins.map((p) => voltageColor(circuit.voltageAt(p.x, p.y), maxV));
        d.leadA = colors[0];
        d.leadB = colors[1];
        d.pinColors = colors;
        if (selected) {
          d.leadA = COL.select;
          d.leadB = COL.select;
          d.pinColors = colors.map(() => COL.select);
        }
      }
      drawElement(d, el);
    }

    // 발열 / 타버림
    if (this.options.showHeat) this.drawHeat(circuit);

    // 접합점
    this.drawJunctions(circuit);

    // 전류 애니메이션
    if (this.options.showCurrent && this.options.running) this.drawCurrentDots(circuit);

    // 값 라벨
    if (this.options.showValues) this.drawLabels(circuit);

    // 미리보기
    if (ov.preview) {
      ctx.save();
      ctx.globalAlpha = 0.55;
      drawElement(
        { ctx, color: '#4ade80', selected: false, highlighted: false, showValues: false, running: false },
        ov.preview,
      );
      ctx.restore();
    }

    // 핀 스냅 표시
    if (ov.cursorGrid) {
      ctx.fillStyle = 'rgba(74,222,128,0.85)';
      ctx.beginPath();
      ctx.arc(ov.cursorGrid.x * GRID, ov.cursorGrid.y * GRID, 3, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();

    // 러버밴드 선택
    if (ov.rubber) {
      const a = this.worldToScreen(ov.rubber.x1, ov.rubber.y1);
      const b = this.worldToScreen(ov.rubber.x2, ov.rubber.y2);
      ctx.fillStyle = 'rgba(96,165,250,0.12)';
      ctx.strokeStyle = 'rgba(96,165,250,0.6)';
      ctx.lineWidth = 1;
      ctx.fillRect(a.x, a.y, b.x - a.x, b.y - a.y);
      ctx.strokeRect(a.x, a.y, b.x - a.x, b.y - a.y);
    }
  }

  private drawGrid(): void {
    const { ctx } = this;
    const step = GRID * this.view.scale;
    if (step < 6) return;
    const w = this.width;
    const h = this.height;
    const startX = ((this.view.ox % step) + step) % step;
    const startY = ((this.view.oy % step) + step) % step;

    ctx.fillStyle = COL.grid;
    for (let x = startX; x < w; x += step) {
      for (let y = startY; y < h; y += step) {
        ctx.fillRect(x - 0.5, y - 0.5, 1, 1);
      }
    }
    // 원점 표시
    const o = this.worldToScreen(0, 0);
    if (o.x > -20 && o.x < w + 20 && o.y > -20 && o.y < h + 20) {
      ctx.strokeStyle = COL.gridStrong;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(o.x - 6, o.y);
      ctx.lineTo(o.x + 6, o.y);
      ctx.moveTo(o.x, o.y - 6);
      ctx.lineTo(o.x, o.y + 6);
      ctx.stroke();
    }
  }

  /** 뜨거운 소자에 주황~빨강 아지랑이, 탄 소자에 그을음 */
  private drawHeat(circuit: Circuit): void {
    const { ctx } = this;
    for (const el of circuit.elements) {
      const b = elementBounds(el);
      const cx = (b.x1 + b.x2) / 2;
      const cy = (b.y1 + b.y2) / 2;
      // 모듈 인스턴스는 안쪽에서 가장 뜨거운 소자 기준
      let temp = el.temp;
      let burnt = el.burnt;
      let tmax = el.thermalSpec()?.tmax ?? 150;
      if (el.type === 'subckt') {
        for (const inner of (el as unknown as { inner: typeof circuit.elements }).inner) {
          if (!inner.thermalSpec()) continue;
          if (inner.temp > temp) {
            temp = inner.temp;
            tmax = inner.thermalSpec()?.tmax ?? 150;
          }
          burnt = burnt || inner.burnt;
        }
      } else if (!el.thermalSpec()) continue;

      if (temp > 45) {
        const k = Math.min(1, (temp - 45) / Math.max(20, tmax - 45));
        const r = 18 + 22 * k;
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        const col = k > 0.7 ? '239,68,68' : k > 0.35 ? '249,115,22' : '251,191,36';
        g.addColorStop(0, `rgba(${col},${0.2 + 0.45 * k})`);
        g.addColorStop(1, `rgba(${col},0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
        if (temp > 60) {
          ctx.save();
          ctx.font = 'bold 9px ui-monospace, monospace';
          ctx.textAlign = 'center';
          ctx.fillStyle = k > 0.7 ? '#fca5a5' : '#fdba74';
          ctx.fillText(`${temp.toFixed(0)}℃`, cx, b.y1 - 5);
          ctx.restore();
        }
      }
      if (burnt) {
        ctx.save();
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, 20);
        g.addColorStop(0, 'rgba(20,16,14,0.85)');
        g.addColorStop(1, 'rgba(20,16,14,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(cx, cy, 20, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#ef4444';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(cx - 6, cy - 6);
        ctx.lineTo(cx + 6, cy + 6);
        ctx.moveTo(cx + 6, cy - 6);
        ctx.lineTo(cx - 6, cy + 6);
        ctx.stroke();
        ctx.font = 'bold 9px ui-sans-serif, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#fca5a5';
        ctx.fillText(el.type === 'liion' ? '열폭주' : '타버림', cx, b.y1 - 5);
        ctx.restore();
      }
    }
  }

  private drawJunctions(circuit: Circuit): void {
    const { ctx } = this;
    const counts = new Map<string, { x: number; y: number; n: number }>();
    for (const el of circuit.elements) {
      for (const p of el.pins) {
        const k = `${p.x},${p.y}`;
        const e = counts.get(k);
        if (e) e.n++;
        else counts.set(k, { x: p.x, y: p.y, n: 1 });
      }
    }
    ctx.fillStyle = COL.junction;
    for (const { x, y, n } of counts.values()) {
      if (n < 3) continue;
      ctx.beginPath();
      ctx.arc(x * GRID, y * GRID, 3.2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private drawCurrentDots(circuit: Circuit): void {
    const { ctx } = this;
    const spacing = 17;
    ctx.fillStyle = '#fde047';
    for (const el of circuit.elements) {
      if (el.pinCount !== 2 || el.usesLayout) continue;
      if (Math.abs(el.current) < 1e-7) continue;
      const g = geometry(el);
      const phase = this.phases.get(el.id) ?? 0;
      const count = Math.max(1, Math.floor(g.len / spacing));
      const offset = ((phase % spacing) + spacing) % spacing;
      for (let i = 0; i < count; i++) {
        const t = (offset + i * spacing) % g.len;
        ctx.beginPath();
        ctx.arc(g.x1 + g.ux * t, g.y1 + g.uy * t, 2.4, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  private drawLabels(circuit: Circuit): void {
    const { ctx } = this;
    ctx.save();
    ctx.font = '10px ui-monospace, SFMono-Regular, monospace';
    ctx.fillStyle = COL.text;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    for (const el of circuit.elements) {
      const label = valueLabel(el);
      if (!label) continue;
      if (el.usesLayout) {
        const b = elementBounds(el);
        ctx.fillText(label, (b.x1 + b.x2) / 2, b.y2 + 4);
        continue;
      }
      const g = geometry(el);
      const vertical = Math.abs(g.uy) > Math.abs(g.ux);
      if (vertical) {
        ctx.textAlign = 'left';
        ctx.fillText(label, g.cx + 16, g.cy - 5);
        ctx.textAlign = 'center';
      } else {
        ctx.fillText(label, g.cx, g.cy + 15);
      }
    }
    ctx.restore();
  }
}

/** 전압 -> 색 (음: 파랑, 0: 회색, 양: 빨강) */
export function voltageColor(v: number, maxAbs: number): string {
  const t = Math.max(-1, Math.min(1, v / Math.max(maxAbs, 1e-9)));
  const k = Math.sign(t) * Math.pow(Math.abs(t), 0.6);
  if (k >= 0) {
    const r = Math.round(148 + (248 - 148) * k);
    const g = Math.round(162 + (113 - 162) * k);
    const b = Math.round(189 + (113 - 189) * k);
    return `rgb(${r},${g},${b})`;
  }
  const a = -k;
  const r = Math.round(148 + (56 - 148) * a);
  const g = Math.round(162 + (189 - 162) * a);
  const b = Math.round(189 + (248 - 189) * a);
  return `rgb(${r},${g},${b})`;
}

/** 전류 크기에 따른 애니메이션 속도 (월드px/초) */
function dotSpeed(current: number): number {
  const a = Math.abs(current);
  if (a < 1e-7) return 0;
  const v = 45 * Math.pow(a / 0.01, 0.35);
  return Math.sign(current) * Math.min(300, Math.max(8, v));
}
